import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

const CONFIRM_WORD = 'DELETE';

type DeleteAgentsConfirmModalProps = {
  open: boolean;
  count: number;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteAgentsConfirmModal: FC<DeleteAgentsConfirmModalProps> = ({
  open,
  count,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!open) setConfirmText('');
  }, [open]);

  const agentNoun = count === 1 ? 'agent' : 'agents';
  const canDelete = confirmText === CONFIRM_WORD && count > 0 && !loading;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: 440,
          maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box px={3} pt={3} pb={2.5} textAlign="center">
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0F172A', mb: 1.5 }}>
          {`Delete ${agentNoun}?`}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#64748B', lineHeight: 1.5 }}>
          This action cannot be undone.
          <br />
          To confirm deletion of {agentNoun}, type{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            DELETE
          </Box>{' '}
          in the box below.
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={confirmText}
          onChange={(event) => setConfirmText(event.target.value)}
          disabled={loading}
          autoFocus
          sx={{
            mt: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
            },
          }}
          inputProps={{ 'aria-label': 'Type DELETE to confirm' }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderTop: '1px solid #E2E8F0',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          disabled={loading}
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderColor: '#E2E8F0',
            color: '#334155',
            backgroundColor: '#FFFFFF',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canDelete}
          onClick={onConfirm}
          sx={{ textTransform: 'none' }}
        >
          {loading ? 'Deleting…' : 'Delete'}
        </Button>
      </Box>
    </Dialog>
  );
};
